import {
  patchProfile,
} from '@zr/utilities/api/profile';

import uploadResume from '@zr/utilities/api/uploadResume';
import deleteResume from '@zr/utilities/api/deleteResume';

import {
  NAME,
  PHONE_NUMBER,
  RESUME,
  RESUME_NAME,
} from '@zr/utilities/profileFields';

// hidden input name that represents use_resume_predictor_validation
export const USE_RESUME_VALIDATION = 'use_resume_validation';
// hidden input that signifies there's no need to upload a resume because we just did in a prior screen
export const USE_ALREADY_UPLOADED_RESUME = 'use_already_uploaded_resume';

// profile_method for tracking in profile patch/resume upload, this should be overriden
const PROFILE_METHOD_DEFAULT = 'fe.applyFlowResumeUtilities';

// checkbox indicating jobseeker has no resume
export const NO_RESUME = 'no_resume';

/**
 * Formik submit handler for both patching profile data and uploading resume
 * @param {boolean} options.alreadyUploadedResume did they already upload a resume? we might need to delete it from our system
 * @param {string} options.method used in profile_method and resume_method
 * @param {function} options.onError callback handling all errors besides validation_error
 * @param {function} options.onStart callback ran at the start, useful for clearing previous errors
 * @param {function} options.onSuccess callback ran when successfully finished
 * @returns formik submit handler
 */
export function handleUpdateProfileAndResumeFormikSubmit({
  alreadyUploadedResume = false,
  method = PROFILE_METHOD_DEFAULT,
  onError,
  onStart = () => {},
  onSuccess,
}) {
  return async (formData, { setStatus, setFieldValue, setSubmitting }) => {
    onStart();

    // resume will be uploaded in a separate endpoint
    const resume = formData[RESUME];
    const resumeName = formData[RESUME_NAME];
    const noResumeChecked = formData[NO_RESUME];
    const useResumeValidation = formData[USE_RESUME_VALIDATION];

    // get profile patch formData
    // this is just NAME and PHONE_NUMBER
    const profileData = {};
    Object.keys(
      formData,
    ).filter(
      (field) => (field === NAME || field === PHONE_NUMBER),
    ).forEach((field) => {
      profileData[field] = formData[field];
    });

    try {
      profileData.profile_method = method;
      await patchProfile(profileData);
    } catch (error) {
      if (error?.data?.fields && error?.data?.error_code === 'validation_error') {
        setStatus(error.data.fields);
      } else {
        onError(error);
      }
      setSubmitting(false);
      // abort early if the first api call fails
      return;
    }

    // if they uploaded a resume prior and then they changed their mind saying no resume
    // delete the resume they just uploaded from our system
    if (alreadyUploadedResume && noResumeChecked) {
      try {
        await deleteResume();
      } catch (error) {
        // dont think we should block them if this actually gets them stuck
      }
    }

    // this function doesn't require a resume
    // that should be required in the form which may state that it's actually optional
    if (!resume) {
      onSuccess();
      return;
    }

    try {
      await uploadResume(resume, resumeName, {
        overwrite: true,
        profileMethod: method,
        resumeMethod: method,
        replaceDefault: true,
        useResumePredictorValidation: useResumeValidation,
      });
      onSuccess();
    } catch (error) {
      if (error?.message) {
        setStatus({
          [RESUME]: error.message,
        });
      }
      // if they try to submit again, relax resume validation (but other errors could still happen)
      setFieldValue(USE_RESUME_VALIDATION, false, false);
      setSubmitting(false);
    }
  };
}

/**
 * Formik submit handler for both patching profile data and uploading resume
 * This also runs whichever callback is appropriate (onNoResumeSuccess or onResumeSuccess)
 * @param {boolean} options.alreadyUploadedResume did they already upload a resume? we might need to delete it from our system
 * @param {string} options.method used in profile_method and resume_method
 * @param {function} options.onError callback handling all errors besides validation_error
 * @param {function} options.onNoResumeSuccess callback ran when successfully updating with no resume specified
 * @param {function} options.onResumeSuccess callback ran when successfully updating with resume specified
 * @param {function} options.onStart callback ran at the start, useful for clearing previous errors
 * @param {function} options.onSuccess callback ran when successfully finished
 * @returns formik submit handler
 */
export const handleResumeFormSubmit = ({
  alreadyUploadedResume = false,
  method,
  onError,
  onNoResumeSuccess,
  onResumeSuccess,
  onStart,
  setIsLoading = () => {},
}) => async (formData, formik) => {
  // `setIsLoading(true)` so that we can disable the close button while the
  // screen is processing/loading
  setIsLoading(true);

  // handle resume/no resume specifically
  const handleFormSuccess = async () => {
    // callback could be async (might call apply endpoint), await keeps isSubmitting true during that
    if (formData[NO_RESUME]) {
      await onNoResumeSuccess();
    } else {
      await onResumeSuccess();
    }
  };

  // await needed to make formik isSubmitting to stay true while this is going on
  await handleUpdateProfileAndResumeFormikSubmit({
    alreadyUploadedResume, method, onError, onStart, onSuccess: handleFormSuccess,
  })(formData, formik);
};
